import type { CharDashaResponse } from "@/lib/api/char-dasha";
import type { ReactNode } from "react";
import AIAstrologerCTA from "@/components/AIAstrologerCTA";
import Link from "next/link";
import { PeriodCard, KarakaCard } from "./_components";

export function CharDashaResult({
  result,
  crossLinks,
}: {
  result: CharDashaResponse;
  crossLinks: ReactNode;
}) {
  const currentPeriod = result.char_dasha_periods.find((p) => p.is_current);

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12">
        <CharDashaHeader result={result} />
        <CurrentDashaCard currentPeriod={currentPeriod} />
        <JaiminiPredictions result={result} />
        <CharDashaSequence result={result} />
        <JaiminiKarakas result={result} />
        <CharDashaCTA />
        <AIAstrologerCTA context="kundli" />
        {crossLinks}
      </div>
    </div>
  );
}

function CharDashaHeader({ result }: { result: CharDashaResponse }) {
  return (
    <div className="text-center mb-8">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3 text-intent-brand">
        Jaimini Char Dasha
      </p>
      <h2 className="text-[26px] sm:text-[32px] font-bold leading-tight font-serif text-ish-dark">
        Char Dasha for {result.name}
      </h2>
      <p className="text-body mt-2 text-ish-text-muted">Lagna: {result.lagna_sign_display}</p>
    </div>
  );
}

function CurrentDashaCard({
  currentPeriod,
}: {
  currentPeriod: CharDashaResponse["char_dasha_periods"][number] | undefined;
}) {
  if (!currentPeriod) return null;
  return (
    <div className="rounded-lg p-5 mb-6 text-center bg-ish-accent-light">
      <p className="text-[11px] font-semibold uppercase tracking-wide mb-1 text-intent-brand">
        Your Current Char Dasha
      </p>
      <p className="text-[22px] font-bold font-serif text-ish-dark">{currentPeriod.sign_display}</p>
      <p className="text-body-small mt-1 text-ish-text-muted">
        Ruled by {currentPeriod.lord_display} · Until {currentPeriod.end_date.slice(0, 7)}
      </p>
      <p className="text-body-small mt-2 italic text-ish-text-muted">{currentPeriod.theme}</p>
    </div>
  );
}

function JaiminiPredictions({ result }: { result: CharDashaResponse }) {
  if (result.jaimini_predictions.length === 0) return null;
  return (
    <div className="card-sacred p-5 sm:p-6 mb-6">
      <h3 className="text-[18px] font-bold mb-4 font-serif text-ish-dark">Jaimini Analysis</h3>
      <div className="space-y-4">
        {result.jaimini_predictions.map((pred, i) => (
          <div key={i}>
            <p className="text-caption font-semibold uppercase tracking-wide mb-1 text-ish-text-faint">
              {pred.topic}
            </p>
            <p className="text-body-small leading-relaxed text-ish-text-muted">{pred.insight}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CharDashaSequence({ result }: { result: CharDashaResponse }) {
  return (
    <div className="card-sacred p-5 sm:p-6 mb-6">
      <h3 className="text-[18px] font-bold mb-2 font-serif text-ish-dark">
        All 12 Char Dasha Periods
      </h3>
      <p className="text-body-small mb-5 text-ish-text-muted">
        Each rashi rules a dasha period whose length equals the distance of its lord from the sign,
        per Jaimini Sutras.
      </p>
      <div className="space-y-3">
        {result.char_dasha_periods.map((period, i) => (
          <PeriodCard key={period.sign} period={period} index={i} />
        ))}
      </div>
    </div>
  );
}

function JaiminiKarakas({ result }: { result: CharDashaResponse }) {
  return (
    <div className="card-sacred p-5 sm:p-6 mb-6">
      <h3 className="text-[18px] font-bold mb-2 font-serif text-ish-dark">Jaimini Karakas</h3>
      <p className="text-body-small mb-5 text-ish-text-muted">
        The 7 karakas are determined by each planet&apos;s degree within its sign. The planet with
        the highest degree becomes the Atmakaraka.
      </p>
      <div className="space-y-2">
        {result.karakas.map((k) => (
          <KarakaCard key={k.role} karaka={k} />
        ))}
      </div>
    </div>
  );
}

function CharDashaCTA() {
  return (
    <div className="rounded-lg p-6 text-center bg-ish-accent-light">
      <p className="text-body font-semibold text-intent-brand">
        Why does your career feel blocked even when you work hard?
      </p>
      <p className="text-body-small mt-1 text-ish-text-muted">
        Your Amatyakaraka and its placement reveal the exact planetary pattern behind it.
      </p>
      <Link
        href="/free-kundali"
        className="btn-sacred inline-block mt-4 px-6 py-2.5 rounded-lg text-body-small font-semibold"
      >
        Full Kundali with AI Chat &rarr;
      </Link>
    </div>
  );
}
